import * as React from 'react';

import { pickBy } from 'lodash';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import type { CustomLabels, FormattingOptions, ReplaceCustomContent } from 'bundles/custom-labels/types/CustomLabels';
import {
  capitalizeEachWord,
  getCustomCourseTerm,
  getCustomWeekTerm,
} from 'bundles/custom-labels/utils/customLabelsMapping';
import { useIsWeekToModuleEnabledForCourse } from 'bundles/custom-labels/utils/featureFlags';

import _t from 'i18n!nls/custom-labels';

export type OutputProps = {
  replaceCustomContent: ReplaceCustomContent;
};

export const useCustomLabelsMapping = (courseId: string, customLabels?: CustomLabels): OutputProps => {
  const isModuleDefault = useIsWeekToModuleEnabledForCourse(courseId);
  const defaultCourseTerm = getCustomCourseTerm();
  const defaultWeekTerm = getCustomWeekTerm(isModuleDefault);
  const defaultCapitalizedCourseTerm = capitalizeEachWord(defaultCourseTerm);
  const defaultCapitalizedWeekTerm = capitalizeEachWord(defaultWeekTerm);

  const getNumberedWeekTerms = (weekNumber: number) => {
    // TODO LRNENG-3369 Update these from week to module upon graduating the week to module project
    // In the meantime, custom-labels v2 will be adjusting the namings on the backend so no FE logic is required
    let weekWithNumber = isModuleDefault
      ? _t('module #{weekNumber}', {
          weekNumber,
        })
      : _t('week #{weekNumber}', {
          weekNumber,
        });

    let capitalizedWeekWithNumber = isModuleDefault
      ? _t('Module #{weekNumber}', {
          weekNumber,
        })
      : _t('Week #{weekNumber}', {
          weekNumber,
        });

    if (customLabels) {
      const { namings, moduleNames, isRhyme } = customLabels;
      const { courseraWeek } = namings;

      const courseraWeekWithCapitalization = capitalizeEachWord(courseraWeek);

      if (isRhyme) {
        weekWithNumber = courseraWeek ?? weekWithNumber;
        capitalizedWeekWithNumber = courseraWeekWithCapitalization ?? capitalizedWeekWithNumber;
      } else if (moduleNames) {
        const { moduleName } = moduleNames.find(({ weekNumber: week }) => week === weekNumber) ?? {};

        if (moduleName) {
          weekWithNumber = moduleName ?? weekWithNumber;
          capitalizedWeekWithNumber = moduleName ?? capitalizedWeekWithNumber;
        }
      } else {
        weekWithNumber = _t('#{courseraWeek} #{weekNumber}', {
          courseraWeek: courseraWeek ?? defaultWeekTerm,
          weekNumber,
        });
        capitalizedWeekWithNumber = _t('#{courseraWeekWithCapitalization} #{weekNumber}', {
          courseraWeekWithCapitalization: courseraWeekWithCapitalization ?? defaultCapitalizedWeekTerm,
          weekNumber,
        });
      }
    }

    return {
      weekWithNumber,
      capitalizedWeekWithNumber,
    };
  };

  // Provide a replacement function as a prop to be used by consumers
  //
  // NOTE: these function signatures must match the ReplaceCustomContent signature in types/CustomLabels.ts
  // They use the 'function' syntax over the arrow syntax to support effective method overloading.
  // Without the overload the conditional return type specified by ReplaceCustomContent
  // will not work and TypeScript will not know how to cast the return values.
  //
  // For more info see:
  // - https://stackoverflow.com/questions/56109614/typescript-overload-arrow-function-is-not-working
  // - https://stackoverflow.com/questions/55059436/typescript-conditional-return-type-based-on-string-argument
  //
  function replaceCustomContent<T extends boolean>(
    message: string,
    options?: FormattingOptions<T>
  ): T extends true ? string : JSX.Element;
  function replaceCustomContent(message: string, options?: FormattingOptions<boolean>) {
    const { additionalVariables = {}, weekNumber, returnsString = false } = options ?? {};

    let labelOverrides = {};

    if (customLabels) {
      const { courseraCourse, courseraWeek } = customLabels.namings;

      const courseraCourseWithCapitalization = capitalizeEachWord(courseraCourse);
      const courseraWeekWithCapitalization = capitalizeEachWord(courseraWeek);

      // There is no guarantee that the customLabelsV2 API will return labels for every
      // term, and since Object.assign() will brute copy over undefined values, we'll
      // use Lodash pickBy() to filter out falsy values. We could write our own helper method,
      // but seeing we have lodash at our disposal, we might as well use it.
      //
      // Read more:
      //   - https://github.com/lodash/lodash/issues/1093#issuecomment-87507761
      //   - https://stackoverflow.com/a/39977485/2466941
      //
      labelOverrides = pickBy({
        ...additionalVariables,
        course: courseraCourse,
        week: courseraWeek,
        capitalizedCourse: courseraCourseWithCapitalization,
        capitalizedWeek: courseraWeekWithCapitalization,
      });
    }

    const variables = {
      ...additionalVariables,
      course: defaultCourseTerm,
      week: defaultWeekTerm,
      capitalizedCourse: defaultCapitalizedCourseTerm,
      capitalizedWeek: defaultCapitalizedWeekTerm,
      ...labelOverrides,
      ...(weekNumber && getNumberedWeekTerms(weekNumber)),
    };

    if (returnsString) {
      return _t(message, variables);
    } else {
      return <FormattedMessage message={message} {...variables} />;
    }
  }

  return {
    replaceCustomContent,
  };
};
